'use client';
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection';
import Button from 'src/components/era-package/forms/Button';
import Buttons from 'src/components/era-package/era/Buttons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RejectionErrorTree from 'src/components/RejectionErrorTree';

const ResponseModal = ({
  title = 'Details',
  open,
  onClose,
  responseData,
  fields,
  optionsMap = {},
}) => {
    const [formData, setFormData] = useState({});
  // responseTabValue: 0 = Raw EDI, 1 = Details
  const [responseTabValue, setResponseTabValue] = useState(0);

  // Reset response sub-tab to Raw EDI whenever modal opens
  useEffect(() => {
    if (open) setResponseTabValue(0);
  }, [open]);
  const dialogRef = useRef(null);

  // Initialize form data when modal opens or responseData changes
  useEffect(() => {
    if (responseData && fields) {
      // Map responseData to form structure
      const mappedData = {};
      fields.forEach(field => {
        mappedData[field.name] = responseData[field.name] || field.defaultValue || '';
      });
      setFormData(mappedData);
    }
  }, [responseData, fields, open]);

  // Handle keyboard navigation (consistent with EditModal)
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose();
    } else if (event.key === 'Tab') {
      // Ensure focus stays within the modal
      const dialog = dialogRef.current;
      if (dialog) {
        const focusableElements = dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  };

    // Don't render if no data
  if (!responseData || !fields) return null;

    // Exclude the 'response' field — rendered separately as tabbed section
  const nonResponseFields = fields.filter((f) => f.name !== 'response');
  const formSection = {
    title: 'Details',
    fields: nonResponseFields,
  };

  // Show Details sub-tab only when response contains rejection errors
  const hasResponseErrors =
    responseData?.response &&
    typeof responseData.response === 'string' &&
    /REJECTED|:\s*[A-Z0-9]{2,6}:/i.test(responseData.response);

  const responseText = responseData?.response ?? '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      ref={dialogRef}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
      // Same focus management as EditModal
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
      onKeyDown={handleKeyDown}
      aria-labelledby="response-modal-title"
      aria-describedby="response-modal-description"
      role="dialog"
    >
      <DialogTitle
        id="response-modal-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {title}
        <IconButton
          aria-label={`Close ${title.toLowerCase()} modal`}
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
          title="Close modal"
        >
          <Box component={FontAwesomeIcon} icon={faTimes} sx={{ width: '1em' }} />
        </IconButton>
      </DialogTitle>

                  <DialogContent dividers id="response-modal-description">
        <Box sx={{ py: 2 }}>
          {/* Form fields — excludes response field */}
          <RenderFormSection
            section={formSection}
            optionsMap={optionsMap}
            formData={formData}
            forceFormData
            setFormData={() => {}}
            errors={{}}
            setErrors={() => {}}
            setHasUnsaved={() => {}}
            globalAccordionAction={null}
            readOnly={true}
          />

          {/* Response field — rendered as tabbed section */}
          {responseText && (
            <Box sx={{ mt: 2 }}>
                            {/* Sub-tabs: Raw EDI | Simplified EDI */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={responseTabValue}
                  onChange={(e, v) => setResponseTabValue(v)}
                  sx={{ minHeight: 36 }}
                >
                  <Tab label="Raw EDI" id="response-tab-0" sx={{ minHeight: 36, py: 0.5 }} />
                                    {hasResponseErrors && (
                    <Tab label="Simplified EDI" id="response-tab-1" sx={{ minHeight: 36, py: 0.5 }} />
                  )}
                </Tabs>
              </Box>

              {/* Raw EDI panel */}
              {responseTabValue === 0 && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.78rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 320,
                    overflowY: 'auto',
                    color: 'text.primary',
                  }}
                >
                  {responseText}
                </Box>
              )}

              {/* Details panel — RejectionErrorTree */}
              {hasResponseErrors && responseTabValue === 1 && (
                <Box sx={{ mt: 1 }}>
                  <RejectionErrorTree responseText={responseText} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ pt: 3 }}>
        <Buttons
          advance={
            <Button
              onClick={onClose}
              variant="contained"
              title={`Close ${title.toLowerCase()}`}
            >
              Close
            </Button>
          }
        />
      </DialogActions>
    </Dialog>
  );
};

export default ResponseModal;